import { action, autorun, computed, makeObservable, observable, toJS } from 'mobx';

import { GAME_LAUNCH_TRIGGER } from '../../config/constants';
import { MonetizationEventsEventType } from '../../dto/monetization-events.types';
import { analytics } from '../../services/analytics';
import { postRequest } from '../../services/api';
import { auth } from '../../services/auth';
import { logToNewRelic } from '../../services/debug';
import { firebaseAuth } from '../../services/firebase';
import { getUserStats, ldClient, resolveFlag } from '../../services/launchDarkly';
import type { ApiResponse } from '../../services/types';
import { devError, devLog, devWarn } from '../../utils/log';
import { type CoinType } from '../cointype/CoinTypeStore';
import type { RootStore } from '../RootStore';
import { Loadable } from '../traits/Loadable';

import { type LaunchGameData } from './types';

export class GameStore extends Loadable {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    super();
    this.rootStore = rootStore;
    makeObservable(this);

    this.resolveAndUpdateGameUiFlag();

    ldClient?.on('ready', () => {
      this.resolveAndUpdateGameUiFlag();
    });

    autorun(() => {
      if (this.rootStore.authStore.isAuthenticationFlowDone && this.rootStore.authStore.authorized) {
        this.resolveAndUpdateGameUiFlag();
      } else {
        this.setGameUiFlag(false);
      }
    });
  }

  lastPlayedGameId: string | undefined;

  @observable
  public gameId: string | undefined;

  @observable
  public selectedGameUrl: string = '';

  @observable
  public selectedSlotGameUrl: string = '';

  @observable
  public currentGame: LaunchGameData | undefined;

  @observable
  public gameUiFlag: boolean = false;

  @observable
  public slotGameIframe: HTMLIFrameElement | null = null;

  @observable
  public slotsGameUiIframe: HTMLIFrameElement | null = null;

  @observable
  public isGameUiError: boolean = false;

  // True while the cash balance is being re-fetched during external-provider slot play (Indiana).
  // The header swaps the cash amount for a reload indicator while this is set.
  @observable
  public isReloadingCash: boolean = false;

  abortController: AbortController | undefined;

  @action
  private resolveAndUpdateGameUiFlag() {
    const gameUiFeatureFlag = resolveFlag('game_ui_web', true);
    this.setGameUiFlag(gameUiFeatureFlag);
  }

  @action
  private setCurrentGame(game?: LaunchGameData) {
    this.currentGame = game;
  }

  private gameUiPendingMessages: { type: string; payload?: Record<string, any> }[] = [];

  private addGameUiPendingMessage(message: { type: string; payload?: Record<string, any> }) {
    this.gameUiPendingMessages.push(message);
  }

  private clearGameUiPendingMessages() {
    this.gameUiPendingMessages = [];
  }

  private isCrownSlotsGame(providerName: string | undefined) {
    return providerName === 'Crownslots';
  }

  @computed
  public get isCrownSlotsActive() {
    return this.isCrownSlotsGame(this.currentGame?.providerName) && Boolean(this.selectedSlotGameUrl);
  }

  // An external-provider slot (Pragmatic, Ruby, …) is loaded directly in the aggregator iframe, not
  // game-ui. Those iframes don't push real-time balance to the host, so the header must poll the
  // cash instead. Internal games (Crownslots / Barouletta) run through game-ui (`showGameUi`) and
  // keep their socket / postMessage real-time updates.
  @computed
  public get isExternalSlotActive(): boolean {
    return Boolean(this.selectedGameUrl) && !this.showGameUi && !this.isCrownSlotsActive;
  }

  @action
  public setGameId(gameId: string | undefined) {
    this.gameId = gameId;
  }

  @action
  private setSelectedGameUrl(url: string) {
    this.selectedGameUrl = url;
  }

  @action
  public setSelectedSlotGameUrl(url: string) {
    this.selectedSlotGameUrl = url;
  }

  @action
  public setGameUiFlag(flag: boolean) {
    this.gameUiFlag = flag;
  }

  @action
  public setSlotGameIframe(iframe: HTMLIFrameElement | null) {
    this.slotGameIframe = iframe;
  }

  @action
  public setSlotsGameUiIframe(iframe: HTMLIFrameElement | null) {
    this.slotsGameUiIframe = iframe;
  }

  @action
  public setIsGameUiError(isError: boolean) {
    this.isGameUiError = isError;
  }

  @action
  public setIsReloadingCash(isReloading: boolean) {
    this.isReloadingCash = isReloading;
  }

  @computed
  public get gameUiUrl() {
    if (!this.gameUiFlag) {
      return '';
    }

    const gameUiUrl = this.rootStore.gameUiStore.getGameUiURL('game', {
      disabledSounds: this.rootStore.uiStore.disabledSounds,
    });

    return gameUiUrl;
  }

  @computed
  public get showGameUi(): boolean {
    return Boolean(this.gameUiFlag && !this.isGameUiError && this.selectedSlotGameUrl);
  }

  public sendSlotsGameUiMessage(
    message: { type: string; payload?: Record<string, any> },
    resendOnIframeLoad: boolean = false,
  ) {
    if (!this.gameUiFlag) {
      return;
    }

    if (this.isGameUiError) {
      devWarn(`Game UI is in error state, cannot send "${message.type}" message`);
      return;
    }

    if (!this.slotsGameUiIframe?.contentWindow) {
      console.warn(`Game UI iframe is not loaded yet, cannot send "${message.type}" message`);

      if (resendOnIframeLoad) {
        this.addGameUiPendingMessage(message);
      }

      return;
    }

    devLog('Sending message to game UI:', message);

    this.slotsGameUiIframe.contentWindow.postMessage(message, import.meta.env.VITE_GAME_UI_URL);
  }

  public async sendCustomTokenToSlotsGameUi() {
    if (!this.gameUiFlag || this.rootStore.gameUiStore.newGameUiAuthEnabled) {
      console.log(
        `Game UI auth: skipped custom token | ${JSON.stringify({ gameUiFlag: this.gameUiFlag, newGameUiAuthEnabled: this.rootStore.gameUiStore.newGameUiAuthEnabled })}`,
      );
      return;
    }

    const customToken = await auth.createCustomToken();

    if (!customToken) {
      console.error('Game UI auth: failed to create custom token');
      return;
    }

    console.log('Game UI auth: sending custom token to game UI');
    this.sendSlotsGameUiMessage(
      {
        type: 'userReady',
        payload: {
          customToken,
          userProfile: getUserStats(this.rootStore.userStore.profile?.stats),
        },
      },
      true,
    );
  }

  public async launchGame(gameId: string, coinType: CoinType, onGameNotFound: () => void) {
    this.setLoading(true);

    try {
      this.sendSlotsGameUiMessage({ type: 'loadingStart' });

      this.abortController = new AbortController();

      const response = await postRequest<ApiResponse<LaunchGameData>>(
        'user/launch-game',
        {
          gameId,
          coin: coinType,
        },
        {
          signal: this.abortController.signal,
        },
      );

      const gameData = response.data?.data;

      if (!gameData) {
        onGameNotFound();
        throw new Error('Game data not found');
      }

      const isInGamePath = window.location.pathname.startsWith('/games');
      if (!isInGamePath) {
        void logToNewRelic(
          'launchGame early-return: not on /games path',
          {
            gameId,
            coinType,
            pathname: window.location.pathname,
            hasGameData: !!gameData,
            gameName: gameData?.gameName,
            providerName: gameData?.providerName,
          },
          'warn',
        );
        this.sendSlotsGameUiMessage({ type: 'loadingEnd' });
        return;
      }

      const trigger =
        this.lastPlayedGameId == gameId
          ? 'coin_change'
          : String(localStorage.getItem(GAME_LAUNCH_TRIGGER) ?? 'direct').toLowerCase();

      void analytics.sendEvent('game_launch', {
        game_id: gameId,
        game_name: gameData.gameName,
        identifier: gameData.identifier,
        game_provider_id: gameData.providerId,
        event_action: 'impression',
        triggered_by: trigger,
      });

      void this.rootStore.metagamesStore.fetch();

      this.setCurrentGame(gameData);

      if (!gameData.gameUrl || !gameData.slotGameUrl) {
        throw new Error('Game URL not found');
      }

      const { gameUrl, slotGameUrl, isGameUi, metagames, providerName } = gameData;

      void this.rootStore.recentlyPlayedStore.localSavePlayedGameId(Number(gameId));

      this.setSelectedGameUrl(gameUrl);
      this.setSelectedSlotGameUrl(slotGameUrl);

      this.setLoading(false);

      if (isGameUi) {
        if (gameData.providerName === 'Crownslots') {
          void this.rootStore.eventTriggerStore.fetch();
        }
        this.sendSlotsGameUiMessage(
          {
            type: 'gameReady',
            payload: {
              gameUrl: slotGameUrl,
              gameId,
              gameProviderName: providerName,
              isBalanceVisible: this.isCrownSlotsActive,
              balance: {
                GC: this.rootStore.walletStore.wallet.gcCoin,
                SC: this.rootStore.walletStore.wallet.totalScCoin,
              },
            },
          },
          true,
        );

        if (metagames) {
          const { token, path, type } = metagames;

          devLog('Sending metagames token to game UI:', { token, path, type });

          this.sendSlotsGameUiMessage(
            {
              type: 'metagamesToken',
              payload: { token, type, eventType: MonetizationEventsEventType.METAGAMES },
            },
            true,
          );
        }
      } else {
        this.sendSlotsGameUiMessage({ type: 'loadingEnd' });
      }
    } catch (err: any) {
      const error = err.response?.data?.errors[0] || err;

      devError('Failed to launch game', error);

      this.resetGame();

      const errorMessage = error.description || error.message;

      if (errorMessage === 'Game not found') {
        onGameNotFound();
      }

      this.setLoading(false);
    } finally {
      this.abortController = undefined;
      this.lastPlayedGameId = gameId;
      localStorage.removeItem(GAME_LAUNCH_TRIGGER);
    }
  }

  public async handleSlotsGameIframeLoad(iframe: HTMLIFrameElement) {
    this.setSlotsGameUiIframe(iframe);

    devLog('Game UI game iframe loaded', { src: iframe.src });

    if (!this.gameUiFlag) {
      return;
    }

    const idToken = await firebaseAuth.currentUser?.getIdToken();

    if (!idToken) {
      devWarn('Handle UI Game iframe, user is not authenticated');
      throw new Error('User is not authenticated');
    }

    this.gameUiPendingMessages.forEach((message) => {
      devLog('Sending pending message to game UI:', message);
      this.sendSlotsGameUiMessage(message);
    });

    this.clearGameUiPendingMessages();

    this.sendSlotsGameUiMessage({
      type: 'setCoinType',
      payload: { coinType: this.rootStore.coinTypeStore.coinType },
    });

    if (this.rootStore.prizeDropStore.prizeDropEvent) {
      this.sendSlotsGameUiMessage({
        type: 'newMetagamesEvent',
        payload: { metagamesEvent: toJS(this.rootStore.prizeDropStore.prizeDropEvent) },
      });
    }

    if (this.rootStore.hammersStore.hammersEvent) {
      this.sendSlotsGameUiMessage({
        type: 'newMetagamesEvent',
        payload: { metagamesEvent: toJS(this.rootStore.hammersStore.hammersEvent) },
      });
    }
  }

  public resetGame() {
    this.abortController?.abort();
    this.setIsReloadingCash(false);
    this.setGameId(undefined);
    this.setCurrentGame(undefined);
    this.setSelectedGameUrl('');
    this.setSelectedSlotGameUrl('');
    this.setSlotGameIframe(null);
    this.sendSlotsGameUiMessage({ type: 'gameExit' });
  }

  // A purchase completed while a slot game is open. Some providers' engines cache the balance and
  // only re-read it on an explicit signal; game-ui maps this to each provider's own message shape
  // (see game-ui `modules/game/providerRefreshBalance.ts`) and drops it for everyone else.
  public refreshSlotBalance() {
    if (!this.selectedSlotGameUrl) {
      return;
    }

    this.sendSlotsGameUiMessage({ type: 'refreshBalance' });
  }

  public updateBalanceInGame() {
    const iframe = document.querySelector('iframe') as HTMLIFrameElement;

    setTimeout(() => {
      try {
        if (this.selectedSlotGameUrl) {
          this.sendSlotsGameUiMessage({
            type: 'updateBalance',
            payload: {
              balance: {
                GC: this.rootStore.walletStore.wallet.gcCoin,
                SC: this.rootStore.walletStore.wallet.totalScCoin,
              },
            },
          });

          if (this.slotGameIframe?.contentWindow && this.isCrownSlotsGame(this.currentGame?.providerName)) {
            this.slotGameIframe.contentWindow.postMessage(
              {
                type: 'updateBalance',
                payload: {
                  balance: {
                    GC: this.rootStore.walletStore.wallet.gcCoin,
                    SC: this.rootStore.walletStore.wallet.totalScCoin,
                  },
                },
              },
              '*',
            );
          }
        }

        if (iframe?.contentWindow) {
          iframe.contentWindow.postMessage('updateBalance', '*');
        }
      } catch (error) {
        devError('Failed to update balance in game', error);
      }
    }, 1000);
  }
}
